import React from 'react';
import { Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import './About.css';
import chePortrait from '../assets/images/band-che.png';
import stewartPortrait from '../assets/images/band-stewart.png';
import calebPortrait from '../assets/images/band-caleb.png';
import usePageTitle from '../hooks/usePageTitle';
import { getLatestNewsPreviews } from '../data/newsArticles';

const About: React.FC = () => {
  usePageTitle('About - The Kults');
  const bandMembers = [
    {
      name: 'Che',
      role: 'Lead Vocals, Guitar, Songwriter',
      image: chePortrait,
      bio: 'At the helm is Che Horgan, an Irish-born guitarist, vocalist, and one of the band’s songwriter’s. Che’s roots run deep in the golden eras of rock ’n’ roll—his sound is steeped in the spirit of the \'50s, \'60s, and \'70s. With a voice that evokes the raw energy of early rock icons and a guitar style that dances between vintage swagger and soulful grit, Che brings an undeniable authenticity to The Kults.'
    },
    {
      name: 'Stewart',
      role: 'Bass, Vocals, Songwriter',
      image: stewartPortrait,
      bio: 'Holding down the low end is fellow Texan Stewart Stevens, a bassist, backing vocalist, and visual artist whose creative vision shapes the band’s identity as much as his music does. Stewart is often seen as the brain behind The Kults—his tenacity, artistic instincts, and songwriting contributions push the band forward both sonically and aesthetically.'
    },
    {
      name: 'Caleb',
      role: 'Guitar, Vocals',
      image: calebPortrait,
      bio: 'Joining him is Caleb Cunningham, a Texas native whose talents span far beyond his role as a vocalist and multi-instrumentalist. As the band’s producer and co-songwriter, Caleb injects a polished, contemporary pop sensibility into the mix. His modern touch bridges generations, turning The Kults’ nostalgic leanings into something refreshingly current.'
    }
  ];

  const achievements = getLatestNewsPreviews(2).map((article) => ({
    year: new Date(article.date).getFullYear().toString(),
    title: article.title,
    description: article.excerpt,
    articleId: article.id,
  }));

  const timeline = [
    {
      year: 'May 2025',
      title: 'Formation',
      description: 'The Kults formed in Basel, Switzerland'
    },
    {
      year: 'July 2025',
      title: 'Debut EP',
      description: 'Planning to release their first EP "The Kults"'
    },
    {
      year: 'September 2025',
      title: 'Production',
      description: 'The Kults are currently recording their debut album between Basel, Switzerland and Texas, USA'
    }
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="container">
          <h1>ABOUT</h1>
          <p>Pushing the boundary with a movement of sound, style and substance</p>
        </div>
      </section>

      <section className="band-intro">
        <div className="container">
          <div className="intro-content">
            <h2>The Kults</h2>
            <p>
            The Kults are an international rock band forging a unique sound that blends timeless nostalgia with a modern pop edge.
            </p>
            <p>
            Formed by a trio of distinct yet complementary creative forces, the band draws from decades of musical history while carving out a vision that feels entirely their own.
            </p>
          </div>
        </div>
      </section>

      <section className="band-members">
        <div className="container">
          <h2>Band Members</h2>
          <div className="members-grid">
            {bandMembers.map((member, index) => (
              <div key={index} className="member-card">
                <div className="member-image">
                  <img src={member.image} alt={member.name} />
                </div>
                <div className="member-info">
                  <h3>{member.name}</h3>
                  <p className="member-role">{member.role}</p>
                  <p className="member-bio">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="achievements">
        <div className="container">
          <h2>Latest</h2>
          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <Link key={index} to={`/news/${achievement.articleId}`} className="achievement-card">
                <div className="achievement-icon">
                  <Award size={30} />
                </div>
                <div className="achievement-content">
                  <h3>{achievement.title}</h3>
                  <p className="achievement-year">{achievement.year}</p>
                  <p className="achievement-description">{achievement.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="legacy">
        <div className="container">
                      <h2>Legacy</h2>
            <div className="legacy-content">
              <p>
              Currently recording their debut album between Basel, Switzerland and Texas, USA, The Kults are preparing to unleash a body of work that reflects their transatlantic influences and collaborative spirit. Set for release in early 2026, the album promises a fusion of contemporary rock/pop with a retro soul—a soundtrack that’s as much a nod to the past as it is a statement of now.
              </p>
              <p>
              The Kults aren't just a band—they’re a movement of sound, style, and substance, their live gigs are fused with artistic visuals that push the boundary past just another rock show. The Kults are poised to leave a lasting imprint on the modern music landscape.
              </p>
              <p>
                
              </p>
            </div>
        </div>
      </section>

      <section className="timeline">
        <div className="container">
          <h2>Timeline</h2>
          <div className="timeline-container">
            {timeline.map((event, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-year">{event.year}</div>
                <div className="timeline-content">
                  <h3>{event.title}</h3>
                  <p>{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 